import { useMemo } from 'react'
import { CALENDAR_MAIN_STAGE } from '@fs/zion-rootstech'
import { getCalendarDetail } from '../../../api'
import { useErrorHandlerOverlay } from '../../../hooks'
import { useLoggedQuery } from '../../../providers/ApolloProviders'
import { useBrightspotImageSizes } from '../../../providers/BrightspotConfigProvider'

// Constant empty array to maintain referential stability when no data
const EMPTY_ARRAY = []

export default function useCalendarData(options) {
  // Support both string argument (legacy) and object argument (current)
  // useCalendarData('main-stage') -> { calendarType: 'main-stage', skip: false }
  // useCalendarData({ calendarType: 'sessions', skip: true }) -> unchanged
  const { calendarType = CALENDAR_MAIN_STAGE, skip = false } =
    typeof options === 'string' ? { calendarType: options, skip: false } : options || {}

  const { promoImage, thumbnailImage, profileImage } = useBrightspotImageSizes()

  const variables = {
    id: `/calendar/${calendarType}`,
    ...profileImage,
    ...promoImage,
    ...thumbnailImage,
  }
  const { loading, data, error } = useLoggedQuery('useCalendarData', getCalendarDetail(), { variables, skip })
  useErrorHandlerOverlay(error)

  /**
   * Memoized extraction of calendar items from CalendarDetail GraphQL response.
   * Maintains referential stability - only creates new array when Apollo data actually changes.
   *
   * Merges items from all stages using flatMap.
   *
   * @returns {Array} Array of calendar items (flat list), or stable EMPTY_ARRAY constant
   */
  const calendarItems = useMemo(() => {
    const calendarDetail = data?.CalendarDetail
    const stages = calendarDetail?.stages

    if (!Array.isArray(stages)) return EMPTY_ARRAY

    // Merge all stages into a single array
    const items = stages.flatMap((stage) => (Array.isArray(stage?.calendarItems) ? stage.calendarItems : []))

    // Return stable empty array constant if no items, otherwise return the flattened array
    return items.length === 0 ? EMPTY_ARRAY : items
  }, [data?.CalendarDetail])

  return { loading, data: calendarItems, error }
}
